import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState } from '../../store';
import DraggableComponent from '../DraggableComponent';
import type { IDefaultAssignmentsDialogProps } from '../../../types';
import { VariableAssignmentsTable } from '../VariableAssignmentsTable';
import { countAssignmentItems } from '../../services/VariableAssignmentsTableHelperFunctions';

/**
 * Dialog component to display default assignments on load or before save
 * @description Shows all configuration variables that are assigned by default values.
 * Rendered on initial configuration load (if defaults exist) and when the user clicks Save/Add Product.
 * - Cancel: closes dialog without saving
 * - Ok: closes dialog and triggers the save/redirect flow
 * @param {IDefaultAssignmentsDialogProps} props - Component props
 * @returns {JSX.Element | null} Default Assignment Dialog component
 */
export const DefaultAssignmentsDialog: React.FC<IDefaultAssignmentsDialogProps> = ({
  assignmentsByDefault,
  lessAssignments,
  onOk,
  onCancel,
}) => {
  const { t } = useTranslation();
  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);

  const lessCount = countAssignmentItems(lessAssignments);
  const regularCount = countAssignmentItems(assignmentsByDefault);

  if (lessCount === 0 && regularCount === 0) {
    return null;
  }

  const classSelector = Number(applicationSettings?.pageSize?.isExtraSmall);
  const flexDirection = ['flex-row', 'flex-column'];
  const alignSelf = ['', 'align-start'];

  const accordionSx = {
    border: '1px solid rgba(0, 0, 0, 0.15)',
    borderRadius: '4px !important',
    '&.MuiAccordion-root': { margin: '8px 0' },
    '&.MuiAccordion-root:before': { display: 'none' },
  };

  const accordionSummarySx = {
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    fontWeight: 600,
    padding: '0 16px',
    '&.Mui-expanded': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
      borderRadius: '4px 4px 0 0',
    },
  };

  const accordionDetailsSx = {
    padding: '8px 16px 16px',
  };

  return (
    <Dialog
      className="AutoSelectedAssignmentDialog-root"
      open={true}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { className: 'AutoSelectedAssignmentDialog-paper' } }}
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle className="header d-flex align-center cursor-move h-auto">
        {t('defaultAssignment.defaultSelectionsFound')}
      </DialogTitle>
      <DialogContent className="dialogContent" style={{ overflowY: 'auto' }}>
        <Box className={`selection ${flexDirection[classSelector]} ${alignSelf[classSelector]}`}>
          <div className="auto-included-message">{t('defaultAssignment.message')}</div>
        </Box>
        {lessCount > 0 && (
          <Accordion defaultExpanded disableGutters sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
              {t('defaultAssignment.pendingSectionTitle', { count: lessCount })}
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsSx}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)', marginBottom: '8px' }}>
                {t('defaultAssignment.pendingSectionInfo', { count: lessCount })}
              </div>
              <div>
                {Object.entries(lessAssignments).map(([productKey, sections]) => (
                  <VariableAssignmentsTable
                    key={productKey}
                    productKey={productKey}
                    sections={sections}
                  />
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
        )}
        {regularCount > 0 && (
          <Accordion defaultExpanded disableGutters sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
              {t('defaultAssignment.regularDefaultsTitle')}
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsSx}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)', marginBottom: '8px' }}>
                {t('defaultAssignment.regularDefaultsNote')}
              </div>
              <div>
                {Object.entries(assignmentsByDefault).map(([productKey, sections]) => (
                  <VariableAssignmentsTable
                    key={productKey}
                    productKey={productKey}
                    sections={sections}
                  />
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>
      <DialogActions className="footer">
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            gap: '8px',
          }}
        >
          <Button className="text-capitalize" color="info" variant="contained" onClick={onOk}>
            {t('button.saveWithDefaults')}
          </Button>
          <Button className="text-capitalize" color="primary" variant="outlined" onClick={onCancel}>
            {t('button.cancel')}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};
